import { toast } from 'vue3-toastify';
import attendanceGridUtils from './attendanceGridUtils';
import getProjections from './getProjections';
import postData from './postData';
import getFatRow from './fatRowGenerator';

const dataProcessor = (data) => {
  function formatTimestamp(dateValue) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    const hours = String(dateValue.getHours()).padStart(2, '0');
    const minutes = String(dateValue.getMinutes()).padStart(2, '0');
    const seconds = String(dateValue.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  function toInclusiveBetweenRange(dateRange) {
    if (!Array.isArray(dateRange) || dateRange.length !== 2) {
      return dateRange;
    }
    const [start, endExclusive] = dateRange;
    if (!start || !endExclusive) {
      return dateRange;
    }
    const endDate = new Date(String(endExclusive).replace(' ', 'T'));
    if (Number.isNaN(endDate.getTime())) {
      return dateRange;
    }
    endDate.setSeconds(endDate.getSeconds() - 1);
    return [start, formatTimestamp(endDate)];
  }

  function buildAttendanceFilters(dateRange, filterValues, filterModel = {}, includeSelectedEmployeeFilter = true) {
    const filters = {
      filterInput: [
        {
          path: 'attendance.attendance_date',
          operator: 'between',
          value: [dateRange[0], dateRange[1]],
        },
      ],
      joinop: 'and',
    };

    if (filterValues.contract !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.allocations_id.contracts_id.id',
        value: [filterValues.contract.id],
      });
    }

    if (filterValues.designation !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.designation.id',
        value: [filterValues.designation.id],
      });
    }

    if (filterValues.department !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.employees_id.departments.id',
        value: [filterValues.department.id],
      });
    }

    if (filterValues.site !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.sites_id.id',
        value: [filterValues.site.id],
      });
    }

    if (filterValues.shift !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.shifts_id.id',
        value: [filterValues.shift.id],
      });
    }

    if (filterValues.attendanceType !== null) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.att_type.id',
        value: [filterValues.attendanceType.id],
      });
    }

    if (filterValues.partners_id !== null) {
      filters.filterInput.push({
        operator: '=',
        path: 'attendance.partners_id',
        value: [filterValues.partners_id],
      });
    }

    if (filterValues.reportingManger !== null) {
      filters.filterInput.push({
        operator: '=',
        path: 'attendance.employees_id.reporting_officer.id',
        value: [filterValues.reportingManger.id],
      });
    }

    const selectedEmployeeId = filterValues?.employee?.id ?? filterValues?.employee;
    if (includeSelectedEmployeeFilter && selectedEmployeeId !== null && selectedEmployeeId !== undefined) {
      filters.filterInput.push({
        operator: 'in',
        path: 'attendance.employees_id.id',
        value: [selectedEmployeeId],
      });
    }

    if (Object.keys(filterModel).length !== 0) {
      const filterInput = [];
      const empFilter = filterModel.employees.filter.split(' ');
      for (let i = 0; i < empFilter.length; i++) {
        filterInput.push({
          operator: 'ilike',
          path: 'attendance.employees_id.persons_id.first_name',
          value: '%' + empFilter[i] + '%',
        });
        filterInput.push({
          operator: 'ilike',
          path: 'attendance.employees_id.persons_id.middle_name',
          value: '%' + empFilter[i] + '%',
        });
        filterInput.push({
          operator: 'ilike',
          path: 'attendance.employees_id.persons_id.last_name',
          value: '%' + empFilter[i] + '%',
        });
        filterInput.push({
          operator: 'ilike',
          path: 'attendance.employees_id.code',
          value: '%' + empFilter[i] + '%',
        });
      }
      filters.filterInput.push({ filterInput, joinop: 'or' });
    }

    return filters;
  }

  async function attendanceList(filterModel, params, monthYear, currentFolder, filterValues, registerView) {
    try {
      const { createTimeRange } = attendanceGridUtils();
      const isWeeklyDateRange = Boolean(filterValues?.weeklyDateRange?.start && filterValues?.weeklyDateRange?.end);
      const rawDateRange = isWeeklyDateRange
        ? [filterValues.weeklyDateRange.start, filterValues.weeklyDateRange.end]
        : createTimeRange(monthYear);
      const dateRange = isWeeklyDateRange ? rawDateRange : toInclusiveBetweenRange(rawDateRange);
      const totalsDateRange = rawDateRange;
      const isWeeklyReviewMode = filterValues?.weeklyReviewMode === true;
      const { projectionForFolder } = getProjections();
      const projections = projectionForFolder('attendance');
      const txnParams = {
        refRows: true,
        page: {
          limitBag: {
            path: 'attendance.employees_id.id',
            limit: registerView ? 1 : (isWeeklyReviewMode ? 5000 : 200),
            offset: isWeeklyReviewMode ? 0 : params.startRow,
            orderBy: 'code',
          },
          getRowCountBag: {
            path: 'attendance.employees_id.id',
            value: true,
          },
          orderByBag: {
            path: 'attendance.employees_id.persons_id.first_name',
            value: 'asc'
          }
        },
        filters: buildAttendanceFilters(
          dateRange,
          filterValues,
          filterModel,
          (registerView || filterValues?.weeklyReviewMode === true)
        ),
        projections: projections,
      };
      console.time('test5');
      const { listData } = postData();
      const list = await listData(currentFolder, txnParams);
      const summary = list.output.data.records[0].summary;
      let totalsSummary = null;

      if (!registerView && !isWeeklyReviewMode) {
        currentFolder.txnToCall = 'txn_attendance_summary_v1';
        const totalsParams = {
          refRows: true,
          filters: buildAttendanceFilters(totalsDateRange, filterValues, filterModel, false),
        };
        const totalsList = await listData(currentFolder, totalsParams);
        totalsSummary = totalsList?.output?.data?.records?.[0] ?? null;
      }

      list.output.data.records = list.output.data.records[0].attendance;
      return { attendanceList : list, attendanceSummary: summary, attendanceTotalsSummary: totalsSummary };
    } catch (e) {
      console.log(e)
    }
  }

  async function dataCreation(store, data, sortModel, attendanceSummary, attendanceTotalsSummary, registerView, timeRange, gridDataSource, employee, empSortField, empSortOrder, currFolder = '', filterValues) {
    try {
      console.time('test1');
      const { fatRowGenerator } = getFatRow();
      const finalData = fatRowGenerator(data)
      let attData = [];
      if (registerView) {
        const currentMonthDays = new Date(timeRange.year, timeRange.month, 0).getDate();
        let color = '';
        for (let i = 0; i < currentMonthDays; i++) {
          const date = new Date(timeRange.year, timeRange.month - 1, i + 1);
          const weekday = date.toLocaleString('en-US', { weekday: 'short' });
          if (weekday === 'Mon') {
            if (color === '') {
              color = '#80808024';
            } else {
              color = '';
            }
          }
          const columnsData = {
            day: i + 1,
            attendance_date:`${i + 1}/${timeRange.month}/${timeRange.year}`,
            dates: `${i + 1} ${weekday}`,
            sites_id: null,
            observed_in_tmstmp: null,
            observed_out_tmstmp: null,
            working_minutes: null,
            att_type: null,
            remarks: null,
            deficit_minutes: null,
            attendance_status: null,
            review_eligible: false,
            latest_exception: null,
            isMissedPunch: true,
            backgroundColor: color,
          }
          attData.push(columnsData);
        }
        for (let i = 0; i < finalData.length; i++) {
          const currentDate = getDate(finalData[i].attendance_date)
          attData[currentDate - 1]['sites_id'] = finalData[i]?.sites_id;
          attData[currentDate - 1]['observed_in_tmstmp'] = finalData[i]?.observed_in_tmstmp;
          attData[currentDate - 1]['observed_out_tmstmp'] = finalData[i]?.observed_out_tmstmp;
          attData[currentDate - 1]['working_minutes'] = finalData[i]?.working_minutes;
          attData[currentDate - 1]['att_type'] = finalData[i]?.att_type;
          attData[currentDate - 1]['deficit_minutes'] = finalData[i]?.difference;
          attData[currentDate - 1]['attendance_status'] = finalData[i]?.attendance_status;
          attData[currentDate - 1]['review_eligible'] = isReviewEligibleAttendance(finalData[i]);
          attData[currentDate - 1]['latest_exception'] = getLatestException(finalData[i]?.exception);
          attData[currentDate - 1]['isMissedPunch'] = false;
          if (finalData[i]?.['exception'] !== null) {
            attData[currentDate - 1]['remarks'] = finalData[i]?.['exception']?.[finalData[i]['exception'].length - 1]?.['description'];
          }
          attData[currentDate - 1]['attendanceData'] = finalData[i];
          if (attData[currentDate - 1]['attendanceData']['departments'] == null) {
            attData[currentDate - 1]['attendanceData']['departments'] = finalData[0]['employees_id'][0]['departments'];
          }
          if (attData[currentDate - 1]['attendanceData']['subsections'] == null) {
            attData[currentDate - 1]['attendanceData']['subsections'] = finalData[0]['employees_id'][0]['subsections'];
          }
          if (attData[currentDate - 1]['attendanceData']['exception'] != null) {
            attData[currentDate - 1]['attendanceData']['exception'].sort((a, b) => {
              if (!a.edit_timestamp && b.edit_timestamp) return -1;
              if (a.edit_timestamp && !b.edit_timestamp) return 1;
              if (!a.edit_timestamp && !b.edit_timestamp) return 0;
              return new Date(a.edit_timestamp) - new Date(b.edit_timestamp);
            });
          }
        }
        if (finalData.length > 0) {
          attData['employees'] = finalData[0]['employees_id'][0];
        }
        else {
          if (employee.id != undefined) {
            try {
              const txnParams = {
                filters: {
                  filterInput: [
                    {
                      path: 'employees.id',
                      operator: '=',
                      value: employee.id
                    }
                  ],
                  joinop: 'and'
                },
                projections: {
                  employees: ['code', 'departments', 'employee_type', 'joining_date', 'persons_id', 'subsections', 'min_working_minutes'],
                  'employees.departments': ['id', 'code', 'name'],
                  'employees.subsections':['id', 'code', 'name'],
                  'employees.employee_type': ['id', 'code', 'name'],
                  'employees.persons_id': ['id',  'last_name', 'birth_date', 'first_name', 'middle_name', 'gender']
                }
              };

              const { listData } = postData();
              const data = await listData(currFolder, txnParams);

              const { fatRowGenerator } = getFatRow();
              const finalData = fatRowGenerator(data);
              const newEmpData = {
                'code': finalData?.[0]?.code,
                'departments' : finalData?.[0]?.departments,
                'employee_type' : finalData?.[0]?.employee_type,
                'id' : finalData?.[0]?.id,
                'joining_date' : finalData?.[0]?.joining_date,
                'min_working_minutes' : finalData?.[0]?.min_working_minutes,
                'persons_id' : finalData?.[0]?.persons_id,
                'subsections' : finalData?.[0]?.subsections,
              }

              attData['employees'] = newEmpData;
            } catch (error) {
              console.error('Error in contractsCalled:', error);
              throw error;
            }
          }
        }
        attData['gridDataSource'] = gridDataSource;
        attData['currEmployee'] = employee;
        attData['attStatus'] = attendanceSummary?.attStatus;
      } else {
        const isWeeklyReviewMode = filterValues?.weeklyReviewMode === true;
        const groupedAttendanceRows = buildEmployeeAttendanceRows(finalData, attendanceSummary, !isWeeklyReviewMode);
        attData = groupedAttendanceRows;

        if (isWeeklyReviewMode) {
          console.timeEnd('test1');
          return attData;
        }
        store.state.attSummary = attendanceTotalsSummary?.grand_total ?? null;
      }
      console.log('ATTDATA', attData);
      if (registerView) {
        return attData;
      }
      //INSERTING DAY WISE SUMMARY TO EMPLOYEES
      attData.forEach((row) => {
        const att_sum = attendanceTotalsSummary?.employees?.[row.employees[0].id]
        if (att_sum) {
          row.att_sum = att_sum
        }
      })
      const { quickSort } = attendanceGridUtils();
      const sortedRow = quickSort(attData, empSortField, empSortOrder);
      console.timeEnd('test1');
      if (sortModel.length !== 0 && sortModel[0].sort !== null) {
        if (sortModel[0].sort === 'desc') {
          sortedRow.reverse();
        }
      }
      return sortedRow;
    }
    catch (e) {
      console.log('error is log', e)
      toast.error(e)
    }
  }

  function dataGenerator(dataForm) {
    const retVal = getFormattedDateField(dataForm.attendance_date);
    return {
      [retVal]: {
        id: dataForm.id,
        attendance_date: dataForm.attendance_date,
        sites_id: dataForm.sites_id,
        shifts_id: dataForm.shifts_id,
        att_type: dataForm.att_type,
        designation: dataForm.designation,
        departments: dataForm.departments,
        subsections: dataForm.subsections,
        working_minutes: dataForm.working_minutes,
        overtime_minutes: dataForm.overtime_minutes,
        exception: dataForm.exception,
        allocations_id: dataForm.allocations_id,
        attendance_status: dataForm.attendance_status,
        in_time: dataForm.observed_in_tmstmp,
        out_time: dataForm.observed_out_tmstmp,
        last_processed_by: dataForm.last_processed_by,
        leave_type: dataForm.leave_type,
        deficit_minutes: dataForm.deficit_minutes,
        review_eligible: isReviewEligibleAttendance(dataForm),
        latest_exception: getLatestException(dataForm?.exception)
      }
    };
  }

  function buildEmployeeAttendanceRows(finalData, attendanceSummary, includeDailyCount = true) {
    const employeeRowsById = new Map();
    finalData.forEach((dataForm) => {
      const employeeId = dataForm?.employees_id?.[0]?.id;
      if (!employeeId) {
        return;
      }
      const currentDayData = dataGenerator(dataForm);
      const rowKey = Object.keys(currentDayData)[0];
      let employeeRow = employeeRowsById.get(employeeId);
      if (!employeeRow) {
        employeeRow = {
          ...currentDayData,
          employees: dataForm.employees_id,
        };
        if (includeDailyCount && employeeRow.employees !== undefined && employeeRow.employees !== null) {
          employeeRow.employees.dailyCount = attendanceSummary?.dailyCount;
        }
        employeeRowsById.set(employeeId, employeeRow);
        return;
      }
      employeeRow[rowKey] = currentDayData[rowKey];
    });
    return Array.from(employeeRowsById.values());
  }

  function getLatestException(exceptionList) {
    if (!Array.isArray(exceptionList) || exceptionList.length === 0) {
      return null;
    }
    const sortedExceptions = [...exceptionList].sort((a, b) => {
      if (!a?.edit_timestamp && !b?.edit_timestamp) return 0;
      if (!a?.edit_timestamp) return -1;
      if (!b?.edit_timestamp) return 1;
      return new Date(a.edit_timestamp).getTime() - new Date(b.edit_timestamp).getTime();
    });
    return sortedExceptions[sortedExceptions.length - 1] ?? null;
  }

  function isReviewEligibleAttendance(attData) {
    const hasException = Array.isArray(attData?.exception) && attData.exception.length > 0;
    const hasManualOrSystem = hasException && attData.exception.some((item) => ['manual', 'system'].includes(item?.type));
    const isUnapproved = attData?.attendance_status?.[0]?.code === 'unapproved';
    return isUnapproved && (hasException || hasManualOrSystem);
  }

  function getFormattedDateField(date) {
    const formattedDate = new Date(date);
    formattedDate.setHours(formattedDate.getHours() + 5);
    formattedDate.setMinutes(formattedDate.getMinutes() + 30);
    const day = formattedDate.getDate();
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[formattedDate.getMonth()];
    const year = formattedDate.getFullYear();
    return `${day}${month}${year}`;
  }

  function getDate(date) {
    const formattedDate = new Date(date);
    formattedDate.setHours(formattedDate.getHours() + 5);
    formattedDate.setMinutes(formattedDate.getMinutes() + 30);
    const day = formattedDate.getDate();
    // const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    // const month = months[formattedDate.getMonth()];
    // const year = formattedDate.getFullYear();
    return day;
  }
  return { attendanceList, dataCreation, getFormattedDateField };
};

export default dataProcessor;
